import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CHART_NEUTRALS, CHART_RAMP } from '../design/tokens';
import { rechartsGrid, rechartsTooltip, rechartsXAxis, rechartsYAxis } from '../design/chartDefaults';

const VisionTrajectoryChart = ({ vision, portfolio, cashflow }) => {
  const chartData = useMemo(() => {
    if (!vision || !vision.target_date) return [];

    const targetDate = new Date(vision.target_date);
    const currentDate = new Date();
    const daysRemaining = Math.max(1, Math.ceil((targetDate - currentDate) / (1000 * 60 * 60 * 24)));

    const currentAmount = vision.current_amount || 0;
    const targetAmount = vision.target_amount || 0;

    const monthlySavings = cashflow?.monthly_savings || cashflow?.investable_amount || 0;
    const monthsRemaining = daysRemaining / 30.44;

    const avgPortfolioReturn = 0.07;
    const monthlyReturn = avgPortfolioReturn / 12;

    const dataPoints = [];
    const numPoints = Math.min(24, Math.ceil(monthsRemaining));

    for (let i = 0; i <= numPoints; i++) {
      const monthsFromNow = (i / numPoints) * monthsRemaining;
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() + monthsFromNow);

      let projectedAmount = currentAmount;
      for (let m = 0; m < monthsFromNow; m++) {
        projectedAmount = projectedAmount * (1 + monthlyReturn) + monthlySavings;
      }

      const optimizedSavings = monthlySavings * 1.2;
      let optimizedAmount = currentAmount;
      for (let m = 0; m < monthsFromNow; m++) {
        optimizedAmount = optimizedAmount * (1 + monthlyReturn) + optimizedSavings;
      }

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      dataPoints.push({
        dateLabel: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
        projected: Math.min(projectedAmount, targetAmount),
        optimized: Math.min(optimizedAmount, targetAmount),
        target: targetAmount,
      });
    }

    return dataPoints;
  }, [vision, portfolio, cashflow]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  if (!vision || !vision.target_date || chartData.length === 0) {
    return (
      <div className="surface-panel p-6">
        <p className="text-body text-secondary text-center py-8">
          Set a target date to see your trajectory chart
        </p>
      </div>
    );
  }

  return (
    <div className="surface-panel p-6">
      <div className="mb-4">
        <h3 className="font-display text-h3 text-primary mb-1">Progress trajectory</h3>
        <p className="text-body text-secondary">
          How your current path compares to an optimized strategy
        </p>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={chartData} margin={{ top: 5, right: 24, left: 8, bottom: 48 }}>
          <CartesianGrid {...rechartsGrid} />
          <XAxis
            dataKey="dateLabel"
            {...rechartsXAxis}
            angle={-35}
            textAnchor="end"
            height={64}
          />
          <YAxis
            {...rechartsYAxis}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip formatter={(v) => formatCurrency(v)} {...rechartsTooltip} />
          <Legend wrapperStyle={rechartsTooltip.labelStyle} iconType="line" />
          <Line
            type="monotone"
            dataKey="projected"
            name="Current path"
            stroke={CHART_RAMP[1]}
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="optimized"
            name="Optimized path"
            stroke={CHART_RAMP[0]}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="target"
            name="Target"
            stroke={CHART_NEUTRALS.inkFaint}
            strokeWidth={1}
            strokeDasharray="3 3"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 flex flex-wrap gap-4 text-label text-secondary">
        <div className="flex items-center gap-2">
          <span className="w-4 h-0.5 bg-pine" aria-hidden />
          Current path
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-0.5 border-t border-dashed border-pine" aria-hidden />
          Optimized path
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-0.5 border-t border-dashed border-hairline" aria-hidden />
          Target
        </div>
      </div>
    </div>
  );
};

export default VisionTrajectoryChart;
